import React from 'react';
import { Box, Typography, IconButton, Button } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { TFunction } from 'react-i18next';
import { BlankModal, LoadingButton } from '..';

interface ConfirmModalProps {
  isSmallDevice: boolean;
  isModalVisible: boolean;
  onModalClose: () => void;
  t: TFunction<'translation'>;
  onConfirm: () => Promise<void>;
  isConfirming: boolean;
  confirmMessage: string;
}

/**
 * Confirm dialogue modal to ask the user whether
 * they want to carry out the selected action
 *
 * @param isSmallDevice - small viewport or not
 * @param isModalVisible - boolean flag if modal is open or not
 * @param onModalClose - function to call when trying to close the modal
 * @param t - the translation function
 * @param onConfirm - submit hander when the Confirm button is pressed
 * @param isConfirming - is the POST request still pending or not
 * @param confirmMessage - the confirmation message to display in the modal
 *
 */
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isSmallDevice,
  isModalVisible,
  onModalClose,
  t,
  onConfirm,
  isConfirming,
  confirmMessage,
}: ConfirmModalProps) => {
  return (
    <BlankModal isSmallDevice={isSmallDevice} open={isModalVisible} showCloseButton={false} handleClose={onModalClose}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography>{t('ns.common:confirm')}</Typography>
        <IconButton onClick={onModalClose}>
          <Close />
        </IconButton>
      </Box>
      <Box>
        <Typography sx={{ mt: 1 }}>{confirmMessage}</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-center',
          justifyContent: 'flex-end',
          gap: 4,
        }}
      >
        <Button onClick={onModalClose} data-testid="confirm-modal-cancel-button">
          {t('ns.common:cancel')}
        </Button>
        <LoadingButton
          color="primary"
          variant="contained"
          onClick={onConfirm}
          data-testid="confirm-modal-confirm-button"
          isLoading={isConfirming}
        >
          {t('ns.common:confirm')}
        </LoadingButton>
      </Box>
    </BlankModal>
  );
};

export default React.memo(ConfirmModal);
